// ImagePreviewModal - Full-screen image lightbox with prev/next navigation
import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const ImagePreviewModal = ({ images, initialIndex = 0, onClose }) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);

    // Reset index when images change
    useEffect(() => {
        setCurrentIndex(initialIndex);
    }, [images, initialIndex]);

    // Legacy single-image support
    const imageList = Array.isArray(images)
        ? images.filter(Boolean)
        : images ? [images] : [];

    const hasPrev = currentIndex > 0;
    const hasNext = currentIndex < imageList.length - 1;

    const goPrev = useCallback(() => {
        if (hasPrev) setCurrentIndex((i) => i - 1);
    }, [hasPrev]);

    const goNext = useCallback(() => {
        if (hasNext) setCurrentIndex((i) => i + 1);
    }, [hasNext]);

    // Keyboard navigation
    useEffect(() => {
        if (imageList.length === 0) return;
        const handler = (e) => {
            if (e.key === 'Escape') onClose?.();
            if (e.key === 'ArrowLeft') goPrev();
            if (e.key === 'ArrowRight') goNext();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [imageList.length, onClose, goPrev, goNext]);

    if (imageList.length === 0) return null;

    const currentSrc = imageList[currentIndex];

    return ReactDOM.createPortal(
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center"
            style={{ animation: 'rednoteFadeIn 0.2s ease-out' }}
        >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/90 backdrop-blur-md" onClick={onClose} />

            {/* Close */}
            <button
                type="button"
                onClick={onClose}
                className="absolute right-5 top-5 z-20 rounded-full bg-white/10 p-2.5 text-white/70 transition-all hover:bg-white/20 hover:text-white"
            >
                <X className="h-5 w-5" />
            </button>

            {/* Counter */}
            {imageList.length > 1 && (
                <div className="absolute top-5 left-1/2 -translate-x-1/2 z-20 rounded-full bg-white/10 px-4 py-1.5 text-xs font-medium text-white/80 backdrop-blur-sm">
                    {currentIndex + 1} / {imageList.length}
                </div>
            )}

            {/* Prev Button */}
            {hasPrev && (
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); goPrev(); }}
                    className="absolute left-4 z-20 rounded-full bg-white/10 p-3 text-white/70 transition-all hover:bg-white/25 hover:text-white hover:scale-110"
                >
                    <ChevronLeft className="h-6 w-6" />
                </button>
            )}

            {/* Next Button */}
            {hasNext && (
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); goNext(); }}
                    className="absolute right-4 z-20 rounded-full bg-white/10 p-3 text-white/70 transition-all hover:bg-white/25 hover:text-white hover:scale-110"
                >
                    <ChevronRight className="h-6 w-6" />
                </button>
            )}

            {/* Image */}
            <div className="relative z-10 max-w-[88vw] max-h-[88vh]" onClick={(e) => e.stopPropagation()}>
                <img
                    key={currentSrc}
                    src={currentSrc}
                    alt={`Preview ${currentIndex + 1}`}
                    className="max-w-full max-h-[88vh] object-contain rounded-xl shadow-2xl ring-1 ring-white/5"
                    style={{ animation: 'rednoteFadeIn 0.25s ease-out' }}
                />
            </div>

            {/* Thumbnail strip */}
            {imageList.length > 1 && (
                <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 flex gap-2 rounded-2xl bg-black/40 p-2 backdrop-blur-md">
                    {imageList.map((src, idx) => (
                        <button
                            key={`thumb_${idx}`}
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setCurrentIndex(idx); }}
                            className={`h-12 w-12 overflow-hidden rounded-lg transition-all ${idx === currentIndex
                                ? 'ring-2 ring-white scale-110'
                                : 'opacity-50 hover:opacity-80'
                                }`}
                        >
                            <img src={src} alt="" className="h-full w-full object-cover" />
                        </button>
                    ))}
                </div>
            )}
        </div>,
        document.body
    );
};

export default ImagePreviewModal;
